/**
 * LoginPage Component
 * VROONG 로그인 페이지 - 사용자 인증 및 시스템 접근을 위한 로그인 화면
 *
 * @description
 * 사용자 아이디/비밀번호 기반 로그인 기능 제공
 * 로그인 상태 유지(Remember Me) 옵션 지원
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastPosition, ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import { UserIcon } from '@/shared/components/icons/Icons';
import PasswordInput from '@/shared/components/forms/PasswordInput';
import FormInput from '@/shared/components/forms/FormInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import LinkButton from '@/shared/components/buttons/LinkButton';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { LOGIN_FEATURES } from '@/features/auth/config/feature.config';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import '@/features/auth/styles/auth.css';

// ============================================================
// 상수 정의
// ============================================================

/** 로그인 성공 후 대시보드 이동 지연 시간 (ms) */
const REDIRECT_DELAY = 500;

/** 비밀번호 찾기 버튼 스타일 */
const FORGOT_PASSWORD_STYLE: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
};

/** 토스트 기본 옵션 */
const TOAST_OPTIONS = {
    position: ToastPosition.TOP_CENTER,
} as const;

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 로그인 페이지 컴포넌트
 */
const LoginPage: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { login, isLoading, lockStatus } = useAuth();
    const lockMin = Math.floor(lockStatus.lockRemainingSeconds / 60);
    const lockSec = lockStatus.lockRemainingSeconds % 60;

    // ======== State ========
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            효율적이고 투명한 라이더 정산 관리
            <br />
            VROONG과 함께 더 나은 배달 서비스를 만들어가세요
        </>
    ), []);

    // ======== Event Handlers ========
    /**
     * 아이디 입력 핸들러
     */
    const handleUserIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUserId(e.target.value.trim());
    }, []);

    /**
     * 비밀번호 입력 핸들러
     */
    const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    }, []);

    /**
     * 로그인 상태 유지 체크박스 핸들러
     */
    const handleRememberMeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setRememberMe(e.target.checked);
    }, []);

    /**
     * 비밀번호 찾기 페이지 이동
     */
    const handleForgotPassword = useCallback(() => {
        navigate('/forgot-password');
    }, [navigate]);

    /**
     * 회원가입 페이지 이동
     */
    const handleRegister = useCallback(() => {
        navigate('/register');
    }, [navigate]);

    /**
     * 로그인 폼 제출 처리
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (lockStatus.isLocked) return;

        // 입력값 검증
        if (!userId || !password) {
            setError('아이디와 비밀번호를 입력해주세요.');
            return;
        }

        try {
            const response = await login(userId, password, rememberMe);

            if (response.success) {
                showToast('로그인이 성공되었습니다.', {
                    type: ToastType.SUCCESS,
                    ...TOAST_OPTIONS,
                });
                setTimeout(() => navigate('/dashboard', { replace: true }), REDIRECT_DELAY);
            } else {
                const errorMsg = response.message || '로그인에 실패했습니다.';
                showToast(errorMsg, { type: ToastType.ERROR, ...TOAST_OPTIONS });
                setError(errorMsg);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err);
            showToast(errorMessage, { type: ToastType.ERROR, ...TOAST_OPTIONS });
            setError(errorMessage);
        }
    }, [userId, password, rememberMe, login, showToast, navigate, lockStatus.isLocked]);

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="라이더 정산 시스템"
                    description={brandDescription}
                    features={LOGIN_FEATURES}
                />

                {/* 우측 로그인 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {/* 헤더 */}
                        <div className="login-header">
                            <h2>로그인</h2>
                            <p>계정에 로그인하여 시스템을 이용하세요</p>
                        </div>

                        {/* 로그인 폼 */}
                        <form className="login-form" onSubmit={handleSubmit}>
                            <FormInput
                                id="userId"
                                name="userId"
                                value={userId}
                                onChange={handleUserIdChange}
                                placeholder="아이디를 입력하세요"
                                label="아이디"
                                required
                                disabled={isLoading || lockStatus.isLocked}
                                icon={<UserIcon />}
                            />

                            <PasswordInput
                                id="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="비밀번호를 입력하세요"
                                required
                                disabled={isLoading || lockStatus.isLocked}
                            />

                            {/* 잠금 / 경고 / 일반 에러 메시지 */}
                            {lockStatus.isLocked ? (
                                <div className="error-message" style={{ textAlign: 'center' }}>
                                    비밀번호를 {lockStatus.maxAttempts}번 잘못 입력하셨습니다.<br />
                                    <strong>{lockMin > 0 ? `${lockMin}분 ` : ''}{lockSec}초</strong> 후 다시 시도해주세요.
                                </div>
                            ) : lockStatus.attemptsRemaining <= 2 && lockStatus.attemptsRemaining > 0 ? (
                                <div className="error-message">
                                    {error && <>{error}<br /></>}
                                    비밀번호를 {lockStatus.maxAttempts - lockStatus.attemptsRemaining}번 잘못 입력하셨습니다.{' '}
                                    <br />
                                    {lockStatus.attemptsRemaining}번 더 실패하면 로그인이 잠깁니다.
                                </div>
                            ) : error ? (
                                <div className="error-message">{error}</div>
                            ) : null}

                            {/* 폼 옵션 */}
                            <div className="form-options">
                                <label className="checkbox-wrapper">
                                    <input
                                        type="checkbox"
                                        id="rememberMe"
                                        name="rememberMe"
                                        checked={rememberMe}
                                        onChange={handleRememberMeChange}
                                        disabled={isLoading}
                                    />
                                    <span className="checkmark" />
                                    <span className="checkbox-text">로그인 상태 유지</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={handleForgotPassword}
                                    className="forgot-password"
                                    style={FORGOT_PASSWORD_STYLE}
                                >
                                    비밀번호 찾기
                                </button>
                            </div>

                            <PrimaryButton type="submit" loading={isLoading} disabled={isLoading || lockStatus.isLocked}>
                                로그인
                            </PrimaryButton>
                        </form>

                        {/* 추가 옵션 */}
                        <div className="additional-options">
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links">
                                <LinkButton onClick={handleRegister}>
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    계정 신청
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    <div className="login-legal-links">
                        <button
                            type="button"
                            onClick={() => navigate('/privacy-policy')}
                            className="legal-link"
                        >
                            개인정보처리방침
                        </button>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
